/**
 * Engage Metric Card
 *
 * Displays a single metric with optional live indicator and trend.
 */

import { motion } from 'framer-motion'

interface EngageMetricCardProps {
  label: string
  value: string | number
  icon: string
  description: string
  trend?: {
    value: number
    direction: 'up' | 'down'
  } | null
  live?: boolean
}

export default function EngageMetricCard({
  label,
  value,
  icon,
  description,
  trend,
  live,
}: EngageMetricCardProps) {
  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}
      className="card p-6 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute -right-4 -top-4 w-24 h-24 bg-engage-primary/5 rounded-full" />

      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-2xl">{icon}</span>
          {live && (
            <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          )}
        </div>

        <p className="text-sm text-gray-500 font-medium">{label}</p>

        <div className="flex items-end gap-2 mt-1">
          <motion.p
            key={String(value)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-gray-900"
          >
            {value}
          </motion.p>

          {trend && (
            <span
              className={`text-sm font-medium mb-1 ${
                trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {trend.direction === 'up' ? '↑' : '↓'} {Math.abs(trend.value)}%
            </span>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-2">{description}</p>
      </div>
    </motion.div>
  )
}
